import { useAppSelector, useAppDispatch } from '@state/hooks'
import {
  triggerGateAdvancement,
  updateStrategyWorkspace,
  addMessageToConversation,
  updateAnalysisReport
} from '@state/slices/conversationsSlice'
import { setGraphContext } from '@state/slices/graphSlice'
import { omapApi } from '@services/omapApi'
import { getNumericCompanyId } from '@utils/companyUtils'
import type { ChatMessage } from '@models/index'
import type { QuickAskQuestionType } from '@utils/quickAskPromptBuilder'
import {
  initializeScenarios,
  selectActiveScenario,
  selectComparisonScenarios,
  selectIsScenarioMode,
  selectShowImpactPanel,
  selectComparisonMode,
  toggleImpactPanel,
  toggleComparisonMode,
  exitScenarioMode
} from '@state/slices/scenariosSlice'
import { Gate1Renderer } from '@components/workspace/gates/Gate1Renderer'
import { Gate2Renderer } from '@components/workspace/gates/Gate2Renderer'
import { Gate3Renderer } from '@components/workspace/gates/Gate3Renderer'
import { MECETableEditorSimple } from '@components/workspace/editors/MECETableEditorSimple'
import { StrategyMapEditorSimple } from '@components/workspace/editors/StrategyMapEditorSimple'
import { ScenarioImpactPanel } from '@components/shared/ScenarioImpactPanel'
import { ScenarioComparisonView } from '@components/shared/ScenarioComparisonView'
import { ProgressModal } from '@components/shared/ProgressModal'
import { IndustryProfilePanel } from '@components/workspace/editors/IndustryProfilePanel'
import { useEffect, useState, useMemo, useRef } from 'react'
import { useIncomingNavigationContext } from '@contexts/TabNavigationContext'
import { scrollToSection } from '@utils/crossTabNavigation'
import { useScrollBookmark } from '@utils/useScrollBookmark'
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '@state/hooks/useIsSampleCompany'
import './StrategyWorkspaceTab.css'

function StrategyWorkspaceTab() {
  const dispatch = useAppDispatch()
  const { activeConversationId, conversations } = useAppSelector(state => state.conversations)
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const companyId = getNumericCompanyId(activeCompanyId)
  const activeConversation = conversations.find(c => c.id === activeConversationId)
  const isSampleCompany = useIsSampleCompany()

  const workspaceData = activeConversation?.strategyWorkspaceData

  const workspaceRef = useRef<HTMLDivElement>(null)

  useScrollBookmark({
    containerRef: workspaceRef,
    conversationId: activeConversationId,
    tabId: 'strategy-workspace',
    ready: Boolean(workspaceData),
    firstVisit: 'bottom',
  })

  // Phase 4: Build execution health map for strategy map overlay
  const executionHealth = useAppSelector(state => state.execution.health)
  const executionHealthMap = useMemo(() => {
    const map: Record<string, 'on_track' | 'at_risk' | 'off_track'> = {}
    for (const entry of executionHealth) {
      // Map by objective_id pattern (F1, C1, etc.) — match against initiative BSC perspective
      if (entry.initiative_id) {
        map[String(entry.initiative_id)] = entry.health
      }
    }
    return map
  }, [executionHealth])

  // Phase 6.5: Scenario mode state
  const [isScenarioMode, setIsScenarioMode] = useState(false)

  // IVP (Industry Profile) section state
  const [isIndustryProfileExpanded, setIsIndustryProfileExpanded] = useState(true)

  // Strategy Map regeneration progress state
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [regenerationCompleted, setRegenerationCompleted] = useState(false)
  const isScenarioInitialized = useAppSelector(state =>
    activeConversationId ? selectIsScenarioMode(state, activeConversationId) : false
  )
  const activeScenario = useAppSelector(state =>
    activeConversationId ? selectActiveScenario(state, activeConversationId) : null
  )
  const showImpactPanel = useAppSelector(state =>
    activeConversationId ? selectShowImpactPanel(state, activeConversationId) : false
  )
  const comparisonMode = useAppSelector(state =>
    activeConversationId ? selectComparisonMode(state, activeConversationId) : false
  )
  const comparisonScenarios = useAppSelector(state =>
    activeConversationId ? selectComparisonScenarios(state, activeConversationId) : null
  )

  // Update local scenario mode state when Redux state changes
  useEffect(() => {
    setIsScenarioMode(isScenarioInitialized)
  }, [isScenarioInitialized])

  // Phase 4.5: Quick Ask handler - sends contextual questions to Quick Mode API
  const handleQuickAsk = async (
    prompt: string,
    questionText: string,
    questionType: QuickAskQuestionType,
    tacticContext: {
      tacticId: string
      tacticTitle: string
      tacticDescription: string
      pillarName: string
      supportingEvidence: string[]
    }
  ) => {
    if (!activeConversationId) return

    // Add user message to conversation
    const userMessage: ChatMessage = {
      id: `msg_quickask_${Date.now()}`,
      role: 'user',
      content: `📋 **Quick Ask** about "${tacticContext.tacticTitle}" (${tacticContext.pillarName})\n\n${questionText}`,
      timestamp: new Date().toISOString(),
    }
    dispatch(addMessageToConversation({
      conversationId: activeConversationId,
      message: userMessage,
    }))

    try {
      // Call Quick Mode API with the contextual prompt
      const response = await omapApi.submitQuickQuery(prompt, activeConversationId, undefined, companyId)

      // Add response to Analysis Report quickResponses
      const currentReport = activeConversation?.analysisReportData || {}
      const quickResponses = currentReport.quickResponses || []

      dispatch(updateAnalysisReport({
        conversationId: activeConversationId,
        data: {
          ...currentReport,
          quickResponses: [
            ...quickResponses,
            {
              question: `[Quick Ask - ${questionType}] ${questionText}`,
              answer: response.response || 'No response received',
              timestamp: new Date().toISOString(),
              tacticId: tacticContext.tacticId,
              tacticTitle: tacticContext.tacticTitle,
              pillarName: tacticContext.pillarName,
            },
          ],
        },
      }))

      // Add assistant message directing user to Analysis Report
      const assistantMessage: ChatMessage = {
        id: `msg_quickask_${Date.now() + 1}`,
        role: 'assistant',
        content: `✅ **Quick Ask Response Ready**\n\n📊 Your question about "${tacticContext.tacticTitle}" has been answered.\n\n**View the full answer in the [Analysis Report](#analysis-report) tab** (Quick Insights section).`,
        timestamp: new Date().toISOString(),
      }
      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: assistantMessage,
      }))

    } catch (error: any) {
      console.error('[StrategyWorkspace] Quick Ask error:', error)

      // Add error message
      const errorMessage: ChatMessage = {
        id: `msg_quickask_error_${Date.now()}`,
        role: 'assistant',
        content: `⚠️ **Quick Ask Error**\n\nSorry, I couldn't process your question about "${tacticContext.tacticTitle}".\n\nError: ${error.message || 'Unknown error'}`,
        timestamp: new Date().toISOString(),
      }
      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: errorMessage,
      }))
    }
  }

  // Phase 6.5: Enter scenario mode
  const handleEnterScenarioMode = () => {
    if (activeConversationId && workspaceData?.meceTable) {
      dispatch(initializeScenarios({
        conversationId: activeConversationId,
        meceTable: workspaceData.meceTable
      }))
      setIsScenarioMode(true)
    }
  }

  // Phase 6.5: Exit scenario mode
  const handleExitScenarioMode = () => {
    if (activeConversationId) {
      dispatch(exitScenarioMode({ conversationId: activeConversationId }))
      setIsScenarioMode(false)
    }
  }

  // Phase 6.5: Toggle impact panel
  const handleToggleImpactPanel = () => {
    if (activeConversationId) {
      dispatch(toggleImpactPanel({ conversationId: activeConversationId }))
    }
  }

  // Phase 6.5: Close comparison view
  const handleCloseComparison = () => {
    if (activeConversationId) {
      dispatch(toggleComparisonMode({ conversationId: activeConversationId }))
    }
  }

  // Phase 2.3: Handle Strategy Map regeneration request when stale
  const handleRegenerateStrategyMap = async () => {
    if (!activeConversationId || !workspaceData?.meceTable) {
      console.warn('[StrategyWorkspace] Cannot regenerate: missing conversation or MECE table')
      return
    }

    // Show progress modal
    setIsRegenerating(true)
    setRegenerationCompleted(false)

    try {
      console.log('[StrategyWorkspace] Triggering Strategy Map regeneration...')

      // Call API to regenerate strategy map from current MECE table
      const response = await omapApi.regenerateStrategyMap(activeConversationId)

      if (response.strategy_map) {
        // Update workspace with new strategy map
        dispatch(updateStrategyWorkspace({
          conversationId: activeConversationId,
          data: {
            ...workspaceData,
            strategyMap: response.strategy_map,
            strategyMapStale: false,
            strategyMapStaleReason: null,
          }
        }))
        console.log('[StrategyWorkspace] Strategy Map regenerated successfully')

        // Signal completion to progress modal (triggers auto-close)
        setRegenerationCompleted(true)
      }
    } catch (error: any) {
      console.error('[StrategyWorkspace] Strategy Map regeneration failed:', error)

      // Hide progress modal on error
      setIsRegenerating(false)

      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: `msg_regen_error_${Date.now()}`,
        role: 'assistant',
        content: `⚠️ **Strategy Map Regeneration Failed**\n\nCould not regenerate the Strategy Map.\n\nError: ${error.message || 'Unknown error'}`,
        timestamp: new Date().toISOString(),
      }
      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: errorMessage,
      }))
    }
  }

  // Handle progress modal close
  const handleRegenerationModalClose = () => {
    setIsRegenerating(false)
    setRegenerationCompleted(false)
  }

  const renderGateContent = (gateData: any) => {
    // Debug logging to help diagnose rendering issues
    console.log('[StrategyWorkspace] renderGateContent called with:', {
      current: gateData?.current,
      title: gateData?.title,
      hasData: !!gateData?.data,
      dataKeys: gateData?.data ? Object.keys(gateData.data) : []
    })

    // Phase 1.1: Updated to accept selected pillars for Gate 3
    const handleApprove = (selectedPillars?: string[]) => {
      if (!activeConversationId) return

      let payload: any = {}

      if (gateData.current === 1) {
        payload = { ddca_scope: gateData.data }
      } else if (gateData.current === 2) {
        payload = { selected_factors: gateData.selectedFactors || [] }
      } else if (gateData.current === 3) {
        payload = {
          mece_table: gateData.data.mece_table,
          strategy_map: gateData.data.strategy_map,
          selected_pillars: selectedPillars,  // Phase 1.1: Pass selected pillars
        }
      }

      dispatch(triggerGateAdvancement({
        conversationId: activeConversationId,
        gate: gateData.current,
        payload,
      }))
    }

    const handleModify = (modifications: any) => {
      dispatch(updateStrategyWorkspace({
        conversationId: activeConversationId!,
        data: {
          ...activeConversation?.strategyWorkspaceData,
          comprehensiveGate: {
            ...gateData,
            data: { ...gateData.data, ...modifications },
          }
        }
      }))
    }

    switch (gateData.current) {
      case 1:
        return <Gate1Renderer data={gateData.data} onApprove={handleApprove} onModify={handleModify} />
      case 2:
        return <Gate2Renderer
          data={gateData.data}
          onApprove={(selectedFactors) => {
            // Store selections before approval
            dispatch(updateStrategyWorkspace({
              conversationId: activeConversationId!,
              data: {
                ...activeConversation?.strategyWorkspaceData,
                comprehensiveGate: {
                  ...gateData,
                  selectedFactors,
                }
              }
            }))
            handleApprove()
          }}
        />
      case 3:
        return (
          <Gate3Renderer
            data={gateData.data}
            onApprove={handleApprove}
            conversationId={activeConversationId!}  // Non-null assertion - we know it exists in gate context
          />
        )
      default:
        // Debug: Log unexpected gate value
        console.error('[StrategyWorkspace] Unexpected gate value:', gateData?.current, 'Type:', typeof gateData?.current)
        return (
          <div className="error-message" style={{ padding: '1rem', color: 'red' }}>
            <h4>⚠️ Unknown Gate State</h4>
            <p>Gate value: {JSON.stringify(gateData?.current)} (type: {typeof gateData?.current})</p>
            <details>
              <summary>Debug Info</summary>
              <pre>{JSON.stringify(gateData, null, 2)}</pre>
            </details>
          </div>
        )
    }
  }

  // Update graph context when workspace data changes
  useEffect(() => {
    const graphContext = workspaceData?.graphContext ||
                        workspaceData?.comprehensiveGate?.data?.graph_context
    if (graphContext) {
      dispatch(setGraphContext(graphContext))
      console.log('[StrategyWorkspace] Updated graph context:', graphContext)
    }
  }, [dispatch, workspaceData?.graphContext, workspaceData?.comprehensiveGate?.data?.graph_context])

  // Handle incoming navigation context (deep-link from Oracle tab)
  const { incomingContext, clearContext } = useIncomingNavigationContext('workspace')

  useEffect(() => {
    if (incomingContext && incomingContext.focusSection) {
      const sectionId = incomingContext.focusSection + '-section'
      console.log('[StrategyWorkspace] Incoming navigation, scrolling to:', sectionId)
      setTimeout(() => {
        scrollToSection(sectionId)
      }, 200)
      clearContext()
    }
  }, [incomingContext, clearContext])

  if (!activeConversationId) {
    return (
      <div className="strategy-workspace-empty">
        <div className="empty-message">
          <h2>Strategy Workspace</h2>
          <p>Visual artifacts will appear here:</p>
          <ul>
            <li>📊 MECE Scenario Analysis Tables</li>
            <li>🎯 Strategy Maps (Balanced Scorecard)</li>
            <li>🔗 Interactive Graph Visualizations</li>
          </ul>
          <p className="hint">Start a conversation and use Comprehensive or Deep mode</p>
        </div>
      </div>
    )
  }

  // Check for actual displayable workspace content (not just graph context from Quick mode)
  const hasDisplayableContent = workspaceData && (
    workspaceData.comprehensiveGate ||
    (workspaceData.meceTable?.pillars?.length > 0) ||
    workspaceData.strategyMapMd ||
    workspaceData.strategyMap
  )

  if (!hasDisplayableContent) {
    return (
      <div className="strategy-workspace-empty">
        <div className="empty-message">
          <h2>Strategy Workspace</h2>
          <p>This tab displays visual artifacts from Strategy Planning mode:</p>
          <ul>
            <li>MECE Scenario Analysis Tables</li>
            <li>Strategy Maps (Balanced Scorecard)</li>
            <li>Interactive Graph Visualizations</li>
          </ul>
          <p className="hint">
            Use Strategy Planning mode (Express, Guided, or Explore) to generate workspace artifacts.
            Quick mode responses appear in the Analysis Report tab.
          </p>
        </div>
      </div>
    )
  }

  // Debug logging for workspace state
  console.log('[StrategyWorkspace] Render state:', {
    hasActiveConversation: !!activeConversation,
    hasWorkspaceData: !!workspaceData,
    hasComprehensiveGate: !!workspaceData?.comprehensiveGate,
    gateValue: workspaceData?.comprehensiveGate?.current,
    gateTitle: workspaceData?.comprehensiveGate?.title,
    hasGateData: !!workspaceData?.comprehensiveGate?.data
  })

  return (
    <div className="strategy-workspace" ref={workspaceRef}>
      <div className="workspace-content">
        {/* Comprehensive Mode Gates */}
        {workspaceData.comprehensiveGate && (
          <section className="workspace-section gate-section">
            <div className="section-header">
              <h3>🚦 Gate {workspaceData.comprehensiveGate.current} - {workspaceData.comprehensiveGate.title || 'Loading...'}</h3>
              <div className="gate-progress">
                Gate {workspaceData.comprehensiveGate.current} of 3
              </div>
            </div>
            <div className="gate-content">
              {renderGateContent(workspaceData.comprehensiveGate)}
            </div>
          </section>
        )}

        {/* MECE Table Section - Final artifacts */}
        {workspaceData.meceTable &&
         workspaceData.meceTable.pillars &&
         workspaceData.meceTable.pillars.length > 0 &&
         !workspaceData.comprehensiveGate && (
          <section id="mece-section" data-bookmark-anchor className="workspace-section mece-section">
            <div className="section-header">
              <h3>📊 MECE Scenario Analysis</h3>
              <div className="section-actions">
                {/* Phase 6.5: Scenario Mode Controls */}
                {!isScenarioMode ? (
                  <button
                    className="action-btn btn-explore-scenarios"
                    onClick={handleEnterScenarioMode}
                    disabled={isSampleCompany}
                    title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Explore different what-if scenarios'}
                  >
                    Explore Scenarios
                  </button>
                ) : (
                  <>
                    <button
                      className={`action-btn btn-impact ${showImpactPanel ? 'active' : ''}`}
                      onClick={handleToggleImpactPanel}
                      title="View scenario impact metrics"
                    >
                      Impact
                    </button>
                    <button
                      className="action-btn btn-exit-scenario"
                      onClick={handleExitScenarioMode}
                      title="Exit scenario exploration mode"
                    >
                      Exit Scenarios
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="mece-table-container">
              <MECETableEditorSimple
                data={workspaceData.meceTable}
                conversationId={activeConversationId}
                onSaveSuccess={() => {
                  console.log('[StrategyWorkspace] MECE table saved successfully')
                }}
                onQuickAsk={handleQuickAsk}
                isScenarioMode={isScenarioMode}
                onToggleScenarioMode={() => setIsScenarioMode(!isScenarioMode)}
              />
            </div>

            {/* Phase 6.5: Scenario Impact Panel (slide-out) */}
            {showImpactPanel && activeScenario && workspaceData.meceTable && (
              <div className="impact-panel-container">
                <ScenarioImpactPanel
                  scenario={activeScenario}
                  meceTable={workspaceData.meceTable}
                  onClose={handleToggleImpactPanel}
                />
              </div>
            )}
          </section>
        )}

        {/* Phase 6.5: Scenario Comparison View (overlay) */}
        {comparisonMode && comparisonScenarios && workspaceData?.meceTable && (
          <div className="comparison-overlay">
            <ScenarioComparisonView
              scenarioA={comparisonScenarios[0]}
              scenarioB={comparisonScenarios[1]}
              meceTable={workspaceData.meceTable}
              onClose={handleCloseComparison}
            />
          </div>
        )}

        {/* Phase 4: Track in Execution button */}
        {workspaceData?.meceTable?.pillars && workspaceData.meceTable.pillars.length > 0 && (
          <div className="track-execution-bar">
            <button
              className="btn-track-execution"
              onClick={() => {
                // Navigate to Execution tab
                const execBtn = Array.from(document.querySelectorAll('.center-tab')).find(
                  el => el.textContent?.includes('Execution')
                ) as HTMLElement
                execBtn?.click()
              }}
            >
              Track in Execution
            </button>
          </div>
        )}

        {/* Strategy Map Section - Final artifacts */}
        {workspaceData.strategyMap &&
         ((workspaceData.strategyMap.perspectives && workspaceData.strategyMap.perspectives.length > 0) ||
          (workspaceData.strategyMap.pillars && workspaceData.strategyMap.pillars.length > 0) ||
          (workspaceData.strategyMap.objectives && workspaceData.strategyMap.objectives.length > 0)) &&
         !workspaceData.comprehensiveGate && (
          <section id="strategy-map-section" data-bookmark-anchor className="workspace-section strategy-map-section">
            <div className="section-header">
              <h3>🎯 Strategy Map</h3>
            </div>
            <div className="strategy-map-container">
              <StrategyMapEditorSimple
                data={workspaceData.strategyMap}
                conversationId={activeConversationId || ''}
                onSaveSuccess={() => {
                  console.log('[StrategyWorkspace] Strategy Map saved successfully')
                }}
                // Phase 2.3: Stale warning props
                isStale={workspaceData.strategyMapStale || false}
                staleReason={workspaceData.strategyMapStaleReason}
                onRegenerateRequest={handleRegenerateStrategyMap}
                executionHealth={executionHealthMap}
              />
            </div>
          </section>
        )}

        {/* Industry Viability Profile Section */}
        {companyId > 0 && (
          <section id="industry-profile-section" className="workspace-section ivp-workspace-section">
            <div
              className="section-header clickable"
              onClick={() => setIsIndustryProfileExpanded(!isIndustryProfileExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsIndustryProfileExpanded(!isIndustryProfileExpanded)
                }
              }}
              aria-expanded={isIndustryProfileExpanded}
            >
              <span className="collapse-icon">{isIndustryProfileExpanded ? '\u25BC' : '\u25B6'}</span>
              <h3>Industry Viability Profile</h3>
            </div>
            {isIndustryProfileExpanded && (
              <div className="section-content">
                <IndustryProfilePanel companyId={companyId} />
              </div>
            )}
          </section>
        )}

      </div>

      {/* Strategy Map Regeneration Progress Modal */}
      <ProgressModal
        isOpen={isRegenerating}
        mode="Strategy Map Regeneration"
        onClose={handleRegenerationModalClose}
        completed={regenerationCompleted}
      />
    </div>
  )
}

export default StrategyWorkspaceTab
